import { formatCurrency, getDineroCurrency } from '../../../../../currency'
import { resolveAssetUrl, type ImageLike } from '../../../../../utils/assetUrl'
import type {
  PlayerDetailsAmount,
  PlayerDetailsResponse,
} from '../../../../../services/player/playerDetailsTables'
import type { PlayerDetailsTables, PlayerFamilyStatus } from '../types'

const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

/** A calendar date as `YYYY-MM-DD`; a longer ISO value keeps its date part. */
const toDateOnly = (value: string | null | undefined): string => {
  const raw = text(value)
  return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw
}

/**
 * "$425.00" / "CA$0.00". The API sends `value` in MAJOR units (dollars);
 * `formatCurrency` takes minor units, so scale by the currency's own exponent.
 */
const formatAmount = (amount: PlayerDetailsAmount | null): string => {
  if (!amount || typeof amount.value !== 'number' || !amount.currency) {
    return ''
  }
  const { exponent } = getDineroCurrency(amount.currency.toUpperCase())
  return formatCurrency({
    amount: Math.round(amount.value * 10 ** exponent),
    currencyCode: amount.currency,
  })
}

/** A logo through the CDN resolver; null when the API sent none. */
const toLogo = (image: ImageLike | undefined): string | null =>
  resolveAssetUrl(image) ?? null

const toFamilyStatus = (status: string): PlayerFamilyStatus =>
  status.toLowerCase() === 'accepted' ? 'accepted' : 'invited'

/** GET /players/:id/details → the rows the five Player Details tables show. */
export const mapPlayerDetailsTables = (
  response: PlayerDetailsResponse
): PlayerDetailsTables => ({
  family: (response.family ?? []).map((member) => ({
    id: member.id,
    name: text(member.name),
    relationship: text(member.relationship),
    email: text(member.email),
    phone: text(member.phone_number),
    status: toFamilyStatus(text(member.status)),
    isAdmin: Boolean(member.is_primary),
  })),
  registrations: (response.registrations ?? []).map((registration) => ({
    id: registration.id,
    competition: text(registration.schedule),
    competitionId: registration.schedule_id ?? null,
    competitionLogo: toLogo(registration.schedule_image),
    division: text(registration.division),
    organization: text(registration.organization),
    organizationId: registration.organization_id ?? null,
    organizationLogo: toLogo(registration.organization_image),
    status: text(registration.status),
    submittedOn: toDateOnly(registration.submitted_at),
    amount: formatAmount(registration.amount),
  })),
  rosters: (response.rosters ?? []).map((roster) => ({
    id: roster.id,
    team: text(roster.team),
    teamId: roster.team_id ?? null,
    teamLogo: toLogo(roster.team_image),
    competition: text(roster.schedule),
    competitionId: roster.schedule_id ?? null,
    competitionLogo: toLogo(roster.schedule_image),
    organization: text(roster.organization),
    organizationId: roster.organization_id ?? null,
    organizationLogo: toLogo(roster.organization_image),
    jerseyNumber: text(roster.player_number),
    position: text(roster.position),
  })),
  suspensions: (response.suspensions ?? []).map((suspension) => ({
    id: suspension.id,
    reason: text(suspension.reason),
    games: suspension.games ?? 0,
    startDate: toDateOnly(suspension.start_date),
    endDate: toDateOnly(suspension.end_date),
    organization: text(suspension.organization),
    status: text(suspension.status),
  })),
  waivers: (response.waivers ?? []).map((waiver) => ({
    id: waiver.id,
    waiver: text(waiver.waiver),
    organization: text(waiver.organization),
    signedBy: text(waiver.signed_by),
    signedOn: toDateOnly(waiver.signed_at),
    status: text(waiver.status),
    hasDocument: Boolean(waiver.has_document),
  })),
})
